import { useState, useEffect } from 'react'
import { Check } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useUserCheck } from '../hooks/api/useUserCheck'
import { DashboardLayout } from '../components/layout/DashboardLayout'
import { supabase } from '../lib/supabase'
import { useSearchParams } from 'react-router-dom'
import { QuoteModal } from '../components/modals/QuoteModal'

const plans = [
  {
    id: 'essentials',
    name: 'Essentials',
    price: '$50/Computer',
    tagline: 'Handle the basics before they become problems',
    description: 'Ideal for small teams or solo operators who want peace of mind and fast support.',
    features: [
      'Remote help desk for end-user support',
      'Real-time hardware and software asset tracking',
      'System health checks and scheduled monitoring',
      'Patch management for software and operating systems',
      'Email spam filtering and malware protection',
      'Antivirus and ransomware protection',
      'SaaS backups (e.g., Microsoft 365)',
      'Basic cybersecurity training',
      'Dark web exposure monitoring',
      'Local and cloud backup with recovery support'
    ],
  },
  {
    id: 'enhanced',
    name: 'Enhanced',
    price: '$75/Computer',
    tagline: 'For growing businesses that can\'t afford IT blind spots',
    description: 'Includes everything in Essentials, plus:',
    features: [
      'Full network audit and optimization',
      'Backup and disaster recovery strategy',
      'Advanced endpoint threat protection',
      'SaaS integration (Microsoft 365, Google Workspace)',
      'Vendor management and third-party coordination',
      'Quarterly business reviews',
      'Advanced email threat protection',
      'Monthly reporting with actionable insights'
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$100/Computer',
    tagline: 'Enterprise-grade protection, built for modern teams',
    description: 'Includes everything in Enhanced, plus:',
    features: [
      '24/7 remote monitoring and support',
      'Infrastructure management across multiple sites',
      'Custom reporting tailored to your business',
      'Cybersecurity awareness training ($4 per user)',
      'Multi-factor authentication setup ($2.80 per user)',
      'Server-level alerting and suspicious activity monitoring ($17 per node)',
      'Full compliance preparation for SOC2, HIPAA, PCI, and CMMC',
      'Policy creation and audit-ready documentation'
    ],
  }
]

export const BillingPage = () => {
  const { user } = useAuth()
  const { data: userRecord, isLoading, error } = useUserCheck(user?.id || '')
  const [searchParams] = useSearchParams()
  const [company, setCompany] = useState<any>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [quoteModal, setQuoteModal] = useState<{
    isOpen: boolean
    planName: string
    planPrice: number
    planDescription: string
  }>({
    isOpen: false,
    planName: '',
    planPrice: 0,
    planDescription: ''
  })

  // Check for success/cancel from Stripe redirect
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccessMessage('Subscription updated successfully!')
      // Clear the URL params
      window.history.replaceState({}, '', '/billing')
    }
    if (searchParams.get('canceled') === 'true') {
      setSuccessMessage('Checkout was canceled.')
      window.history.replaceState({}, '', '/billing')
    }
  }, [searchParams])

  // Fetch and sync company subscription data
  useEffect(() => {
    const fetchAndSyncCompany = async () => {
      if (!userRecord?.company_id) return

      // First, sync subscription from Stripe
      try {
        await supabase.functions.invoke('stripe-verify-session', {
          body: { company_id: userRecord.company_id }
        })
      } catch (err) {
        console.log('Sync skipped:', err)
      }

      // Then fetch the updated company data
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, subscription_tier, subscription_status, stripe_customer_id, subscription_period_end')
        .eq('id', userRecord.company_id)
        .single()

      if (!error && data) {
        setCompany(data)
      }
    }

    fetchAndSyncCompany()
  }, [userRecord?.company_id])

  const handleManageSubscription = async () => {
    if (!userRecord?.company_id) return

    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing-portal', {
        body: {
          company_id: userRecord.company_id,
          return_url: `${window.location.origin}/billing`,
        }
      })

      if (error) throw error

      if (data?.url) {
        window.location.href = data.url
      }
    } catch (err: any) {
      console.error('Failed to open billing portal:', err)
      setSuccessMessage(`Error: ${err.message || 'Failed to open billing portal'}`)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-3 border-gray-400 border-t-transparent border-r-transparent mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading billing information...</p>
        </div>
      </div>
    )
  }

  if (error || !userRecord) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-2">User not found in database!</p>
            <p className="text-red-500 text-xs mb-2">This account was created before user record creation was implemented.</p>
            <p className="text-red-500 text-xs">Please create a new account to test the full flow.</p>
          </div>
        </div>
      </div>
    )
  }

  const currentTier = company?.subscription_tier || 'essentials'
  const currentPlan = plans.find(plan => plan.id === currentTier) || plans[0]
  const subscriptionStatus = company?.subscription_status || 'inactive'

  return (
    <DashboardLayout
      userRole={userRecord.role}
      adminLevel={userRecord.admin_level}
      userName={userRecord.full_name || user?.email}
      userEmail={user?.email}
    >
      <div className="space-y-8">
        {/* Success/Error Message */}
        {successMessage && (
          <div className={`p-4 rounded-lg ${
            successMessage.includes('Error')
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {successMessage}
            <button
              onClick={() => setSuccessMessage(null)}
              className="ml-4 text-sm underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Billing & Subscription</h1>
          <p className="text-gray-600 mt-1">Manage your subscription, payments, and billing information.</p>
        </div>

        {/* Current Plan Section */}
        <div className="bg-white">
          <div className="flex items-start justify-between mb-8">
            <div>
              <h2 className="text-3xl font-semibold text-black mb-2">Current Plan</h2>
              <h3 className="text-2xl font-medium text-black mb-1">{currentPlan.name}</h3>
              <p className="text-gray-400">Up to 50 devices</p>
            </div>
            <div className="text-right">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                subscriptionStatus === 'active'
                  ? 'bg-green-100 text-green-800'
                  : subscriptionStatus === 'past_due'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {subscriptionStatus === 'active' ? 'Active' :
                 subscriptionStatus === 'past_due' ? 'Past Due' :
                 subscriptionStatus === 'canceled' ? 'Canceled' : 'Inactive'}
              </span>
              <div>
                <p className="text-3xl font-medium text-black">{currentPlan.price.split('/')[0]}</p>
                <p className="text-gray-400 text-lg">per computer/month</p>
              </div>
            </div>
          </div>

          {/* Features Box */}
          <div className="bg-[#fafafa] border border-gray-200 rounded-3xl p-6 mb-8">
            <ul className="space-y-3">
              {currentPlan.features.map((feature, index) => (
                <li key={index} className="flex items-center text-gray-400">
                  <Check className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Manage Subscription Button */}
          {company?.stripe_customer_id && (
            <button
              onClick={handleManageSubscription}
              className="px-6 py-3 bg-gray-900 text-white rounded-full hover:bg-gray-800 transition-colors text-sm font-medium"
            >
              Manage Subscription
            </button>
          )}
        </div>

        {/* Plan Options Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Available Plans</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl">
            {plans.map((plan) => {
              const isCurrentPlan = plan.id === currentTier

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl p-6 lg:p-8 transition-all duration-200 flex flex-col border-2 ${
                    isCurrentPlan
                      ? 'border-red-500 shadow-md'
                      : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                >
                  {/* Plan Header */}
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <div className="text-xl font-medium text-gray-900 mb-2">{plan.price}</div>
                    <p className="text-gray-400 text-sm leading-relaxed">{plan.tagline}</p>
                  </div>

                  {/* Features List */}
                  <div className="flex-1 mb-8">
                    <ul className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <Check className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0 mr-3" />
                          <span className="text-gray-400 text-xs">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => {
                      const priceNum = parseInt(plan.price.replace(/[^0-9]/g, ''))
                      setQuoteModal({
                        isOpen: true,
                        planName: plan.name,
                        planPrice: priceNum,
                        planDescription: plan.tagline
                      })
                    }}
                    className="w-full px-6 py-4 rounded-full font-medium transition-colors duration-200 text-sm flex items-center justify-center bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Request a Quote
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Quote Modal */}
      <QuoteModal
        isOpen={quoteModal.isOpen}
        onClose={() => setQuoteModal(prev => ({ ...prev, isOpen: false }))}
        planName={quoteModal.planName}
        planPrice={quoteModal.planPrice}
        planDescription={quoteModal.planDescription}
        userEmail={user?.email}
        userName={userRecord?.full_name}
        companyId={userRecord?.company_id}
      />
    </DashboardLayout>
  )
}
